import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { routes } from 'utils/routing';
import { useRouter } from 'next/router';
import { Typography } from '@mui/material';

const useStyles = makeStyles()(() => ({
  container: {
    color: '#2563eb',
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
  org: {
    display: 'inline',
    marginRight: '0.25em',
    fontWeight: 500,
    textDecoration: 'unset',
    cursor: 'pointer',
    '&:hover': {
      color: '#3449A9',
    },
  },
  more: {
    fontWeight: 500,
    marginLeft: '0.25em',
    whiteSpace: 'nowrap',
  },
  probe: {
    position: 'absolute',
    top: -9999,
    left: -9999,
    visibility: 'hidden',
    lineHeight: 1.4,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
}));

type Org = { id: number | string; title: string };

interface Props {
  eventId: number;
  orgs: Org[];
  lines?: number;
}

const OrgsBox: React.FC<Props> = ({ eventId, orgs, lines = 3 }) => {
  const { classes, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<Org[]>([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const router = useRouter();

  useEffect(() => {
    if (!containerRef.current || !probeRef.current) return;

    const measure = () => {
      const container = containerRef.current;
      const probe = probeRef.current;
      if (!container || !probe) return;
      const lh = parseFloat(getComputedStyle(container).lineHeight);
      const maxH = lh * lines + 2;

      // mirror width
      probe.style.width = `${container.offsetWidth}px`;

      let fit = orgs.length;

      // fallback: always at least one visible
      if (orgs.length === 0) {
        setVisible([]);
        setHiddenCount(0);
        return;
      }

      for (let i = orgs.length; i >= 1; i -= 1) {
        const hidden = orgs.length - i;
        probe.innerHTML =
          orgs
            .slice(0, i)
            .map((o, j) => `${o.title}${j < i - 1 ? ', ' : ''}`)
            .join('') + (hidden > 0 ? ` <span style='color:#6b7280;white-space:nowrap;'>+${hidden} more</span>` : '');

        if (probe.scrollHeight <= maxH) {
          fit = i;
          break;
        }
      }

      const hidden = Math.max(orgs.length - fit, 0);
      const vis = orgs.slice(0, Math.max(fit, 1)); // ensure at least one
      setVisible(vis);
      setHiddenCount(hidden);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [orgs, lines]);

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>, route: any) => {
    event.preventDefault();
    event.stopPropagation();
    router.push(route.href);
  };

  return (
    <>
      <div ref={probeRef} className={classes.probe} />
      <div ref={containerRef} className={classes.container}>
        {visible.map((o, i) => (
          <Typography
            key={o.id}
            className={cx(classes.org)}
            color="primary"
            variant="body2"
            component="span"
            onClick={(event: React.MouseEvent<HTMLSpanElement>) => handleClick(event, routes.organizationProfile(o.id))}
          >
            {o.title}
            {i < visible.length - 1 ? ',' : ''}{' '}
          </Typography>
        ))}
        {hiddenCount > 0 && (
          <Typography
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClick(e, routes.event(eventId));
            }}
            className={classes.more}
            color="primary"
            variant="body2"
            component="span"
          >
            +{hiddenCount} more
          </Typography>
        )}
      </div>
    </>
  );
};

export default OrgsBox;
